import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  NewCard,
  Card,
  CREDIT_CARD_FIELDS_NAMES as CreditCardFields,
  CREDIT_CARD_DEFAULT_VALUES,
} from '@ott/credit-card';
import { prepareRHFErrorsDeeply } from '@ott/first-error-shower';

import { FORM_FIELDS } from '../helpers/constants';
import { selectRefillFormValidation } from '../helpers/scheme';
import { RefillFormType } from '../types';

export const useRefillForm = ({ amount }: { amount: number }) => {
  const [submitTime, setSubmitTime] = useState(Date.now());

  const formResolver = useSelector(selectRefillFormValidation);

  const form = useForm<RefillFormType>({
    defaultValues: {
      amount: amount ? String(amount) : undefined,
      ...CREDIT_CARD_DEFAULT_VALUES,
    },
    resolver: formResolver,
  });

  const [cardId, cardNumber, cardHolder, year, month, cvv, saveCard] = form.watch([
    FORM_FIELDS.selectedId,
    FORM_FIELDS.cardNumber,
    FORM_FIELDS.cardHolder,
    FORM_FIELDS.year,
    FORM_FIELDS.month,
    FORM_FIELDS.cvv,
    FORM_FIELDS.saveCard,
  ]);

  const cardValues = {
    [FORM_FIELDS.cardNumber]: cardNumber,
    [FORM_FIELDS.cardHolder]: cardHolder,
    [FORM_FIELDS.month]: month,
    [FORM_FIELDS.year]: year,
    [FORM_FIELDS.cvv]: cvv,
    [FORM_FIELDS.saveCard]: saveCard,
  };

  const preparedCard = Object.keys(cardValues).reduce((acc, key) => {
    const creditCardKey = key as typeof CreditCardFields[keyof typeof CreditCardFields];
    // @ts-ignore
    if (cardValues[creditCardKey] !== undefined) {
      // @ts-ignore
      acc[creditCardKey] = cardValues[creditCardKey];
    }

    return acc;
  }, {} as Card);

  const setActiveCard = useCallback(
    (card: Card) => {
      const newCard = card as NewCard;

      form.setValue(FORM_FIELDS.cardNumber, newCard.cardNumber, { shouldValidate: true });
      form.setValue(FORM_FIELDS.cardHolder, newCard.cardHolder ?? '', {
        shouldValidate: true,
      });
      form.setValue(FORM_FIELDS.cvv, newCard.cardCVV, { shouldValidate: true });
      form.setValue(FORM_FIELDS.month, newCard.expMonth, { shouldValidate: true });
      form.setValue(FORM_FIELDS.year, newCard.expYear, { shouldValidate: true });

      if ('saveCard' in card) {
        form.setValue(FORM_FIELDS.saveCard, Boolean(card.saveCard), { shouldValidate: true });
      }
    },
    [form.setValue]
  );

  useEffect(() => {
    if (form.formState.submitCount) {
      setSubmitTime(Date.now());
    }
  }, [form.formState.submitCount]);

  const preapredErrors = prepareRHFErrorsDeeply(form.formState.errors);

  return {
    form,
    cardValues,
    preparedCard,
    setActiveCard,
    cardId,
    preapredErrors,
    submitTime,
  };
};
